import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BenefitPlan, BenefitEnrollment } from "@/types/payroll";
import { Heart, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export function BenefitsEnrollment() {
  const [plans] = useKV<BenefitPlan[]>("payroll-benefit-plans", [
    {
      id: "plan-health-gold",
      name: "Gold Health Plan",
      type: "Health",
      provider: "BlueCross",
      coverageAmount: 1000000,
      employeeCost: 150,
      employerCost: 450,
      description: "Comprehensive health coverage with low deductible."
    },
    {
      id: "plan-dental-basic",
      name: "Basic Dental",
      type: "Dental",
      provider: "Delta Dental",
      coverageAmount: 2000,
      employeeCost: 20,
      employerCost: 40,
      description: "Preventive care and basic procedures."
    }
  ]);

  const [enrollments, setEnrollments] = useKV<BenefitEnrollment[]>("payroll-benefit-enrollments", []);

  const handleEnroll = (planId: string) => {
    const enrollment: BenefitEnrollment = {
      id: crypto.randomUUID(),
      employeeId: "CURRENT_USER",
      planId: planId,
      enrolledAt: new Date().toISOString(),
      status: "Active"
    };
    setEnrollments([...enrollments, enrollment]);
    toast.success("Enrolled successfully!");
  };

  const handleCancel = (planId: string) => {
    setEnrollments(enrollments.filter(e => e.planId !== planId));
    toast.success("Enrollment cancelled");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-semibold">Open Enrollment</h3>
        <p className="text-sm text-slate-500">Select your benefits for the upcoming year.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isEnrolled = enrollments.some(e => e.planId === plan.id && e.status === 'Active');
          
          return (
            <Card key={plan.id} className={`border-2 ${isEnrolled ? 'border-green-500' : 'border-transparent'}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <Badge variant="outline">{plan.type}</Badge>
                  {isEnrolled && <Badge className="bg-green-500 hover:bg-green-600"><Check className="mr-1" /> Enrolled</Badge>}
                </div>
                <CardTitle className="mt-2">{plan.name}</CardTitle>
                <CardDescription>{plan.provider}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">{plan.description}</p>
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Your Cost</span>
                    <span className="font-semibold">${plan.employeeCost}/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Company Pays</span>
                    <span className="text-green-600 font-medium">${plan.employerCost}/mo</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                {isEnrolled ? (
                  <Button variant="outline" className="w-full text-red-500 hover:text-red-700" onClick={() => handleCancel(plan.id)}>
                    Cancel Coverage
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => handleEnroll(plan.id)}>
                    Select Plan
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
